import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand, useProductName } from "@/context/BrandContext";
import api, { formatApiError } from "@/lib/api";
import { isAgentAccount } from "@/lib/agentAccount";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Eye, EyeSlash, Headset } from "@phosphor-icons/react";
import CaptchaWidget, { resetCaptcha } from "@/components/CaptchaWidget";

function BrandMark({ brand, productName }) {
  const src = brand.login_logo_url || brand.logo_url;
  if (src) {
    return (
      <div className="flex items-center gap-3">
        <img src={src} alt={productName || "Logo"} className="w-11 h-11 object-contain" />
        <div className="leading-tight">
          <p className="font-display font-bold tracking-tight text-xl">{productName}</p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-primary-foreground/70">Agent Login</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center">
        <Headset className="w-6 h-6" weight="fill" />
      </div>
      <div className="leading-tight">
        <p className="font-display font-bold tracking-tight text-xl">{productName || "WACalls"}</p>
        <p className="text-[10px] uppercase tracking-[0.22em] text-primary-foreground/70">Agent Login</p>
      </div>
    </div>
  );
}

export default function AgentLogin() {
  const { user, ready, refresh } = useAuth();
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  const onCaptcha = useCallback((token) => setCaptchaToken(token), []);
  const onCaptchaEnabled = useCallback((enabled) => {
    setCaptchaRequired(!!enabled);
    if (!enabled) setCaptchaToken(null);
  }, []);

  useEffect(() => {
    if (ready && user && isAgentAccount(user)) {
      navigate("/agent/select-campaign", { replace: true });
    }
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) {
      setError("Username is required.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (captchaRequired && !captchaToken) {
      setError("Please complete the captcha.");
      return;
    }
    setLoading(true);
    try {
      const body = { username: username.trim(), password };
      if (captchaToken) body.captcha_token = captchaToken;
      const { data } = await api.post("/agent/login", body);
      localStorage.setItem("wa_token", data.access_token);
      await refresh();
      navigate("/agent/select-campaign", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err) || err.message);
      resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="agent-login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -left-16 w-80 h-80 rounded-full bg-black/10 blur-3xl" />
        <BrandMark brand={brand} productName={productName} />
        <div className="relative">
          <h1 className="text-4xl font-display font-bold leading-tight tracking-tight">
            Agent workspace
          </h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Sign in to join an active campaign, connect your session, and take customer calls.
          </p>
        </div>
        <p className="text-xs text-primary-foreground/60 relative">
          © {new Date().getFullYear()} {productName || "WACalls"} · Agent Interface
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {(brand.logo_url || brand.login_logo_url) ? (
              <img src={brand.logo_url || brand.login_logo_url} alt="" className="w-10 h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground grid place-items-center">
                <Phone className="w-5 h-5" weight="fill" />
              </div>
            )}
            <div>
              <p className="font-display font-bold">{productName || "WACalls"}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Agent Login</p>
            </div>
          </div>

          <h2 className="text-2xl font-display font-semibold tracking-tight">Agent sign in</h2>
          <p className="text-sm text-muted-foreground mt-1.5">
            Use your agent username (email or SIP username) and password.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="agent-username">Username</Label>
              <Input
                id="agent-username"
                type="text"
                autoComplete="username"
                value={username}
                required
                onChange={(e) => setUsername(e.target.value)}
                data-testid="agent-login-username"
                placeholder="email or SIP username"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-password">Password</Label>
              <div className="relative">
                <Input
                  id="agent-password"
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="agent-login-password"
                  placeholder="••••••••"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <CaptchaWidget onChange={onCaptcha} onEnabled={onCaptchaEnabled} />
            {error && (
              <p className="text-sm text-destructive" data-testid="agent-login-error">{error}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="agent-login-submit"
              className="w-full h-11 rounded-lg font-medium"
            >
              {loading ? "Signing in…" : "Login"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Portal users?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Customer / Admin sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
